import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AnyoComponent } from 'projects/app-core/src/app/models/AnyoComponent';
import { AuthService } from 'projects/app-core/src/auth/auth.service';
import { ToastService } from 'projects/app-core/src/app/services/toastr.service';
import { IAnyoError } from 'projects/app-core/src/models/errorModal';

import {
  GoalsService,
  PlanProgressorMonitorGridItem,
  PlanProgressorMonitorGridRequest,
  PlanProgressorMonitorGridResponse,
  UserObjectiveStatus,
  UserObjectivesProgressBucket,
  UserTags,
} from '../../../services/goals.service';

/**
 * APH-4995 T10 — fleet-wide "who needs attention" dashboard for real users' Plan Progressor
 * outcomes. Standalone page (not nested in user-profile search, that's T5's per-user drill-down),
 * mirroring manage-rules.component.ts's list/grid pattern (URL-encoded filter state, backend
 * pagination) exactly, since this is the closest existing precedent for a filterable admin grid.
 * User/partner/tags/title/description/status/progress columns and their column-filter dropdowns
 * mirror user-objectives-list.component.ts's own later addition of the same set over the same
 * underlying collection — kept as separate column filters here rather than folded into the
 * existing flags/hold-reason toolbar, since those two filter families answer different questions
 * ("who needs attention" vs "find this specific row").
 */
@Component({
  selector: 'app-plan-progressor-monitor',
  templateUrl: './plan-progressor-monitor.component.html',
})
export class PlanProgressorMonitorComponent extends AnyoComponent {
  constructor(
    private goalsSvc: GoalsService,
    auth: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private toastrService: ToastService,
  ) {
    super(auth);
  }

  UserObjectivesProgressBucket = UserObjectivesProgressBucket;
  statusOptions = Object.values(UserObjectiveStatus);
  progressBucketOptions = Object.values(UserObjectivesProgressBucket);
  userTagsOptions = Object.values(UserTags);

  tableLoading = false;
  page = 1;
  pageSize = 20;
  totalRecords = 0;
  pageSizeOptions = [20, 50, 100];
  data: PlanProgressorMonitorGridItem[] = [];

  // Guards against an older, slower fetch() response overwriting a newer one
  // (e.g. rapid pagination/filter clicks); only the response matching the
  // most recently issued request id is applied.
  private fetchRequestId = 0;

  // Each is independently toggle-able; selected filters are OR'd server-side (see
  // planProgressorMonitorService.ts's own comment on this — "who needs attention" semantics).
  filterHandback = false;
  filterTerminal = false;
  filterDisengage = false;
  filterDormant = false;
  filterRebaseline = false;
  filterRampWithoutDestination = false;

  // Column search dropdowns (regex, text input + Ok/Reset).
  expertTitleSearch = '';
  displayTitleSearch = '';
  userEmailSearch = '';
  partnerIdSearch = '';

  // Column filter dropdowns (checkbox multi-select).
  statusFilterValues: UserObjectiveStatus[] = [];
  userTagsFilterValues: string[] = [];
  progressBucketFilterValues: UserObjectivesProgressBucket[] = [];

  // Default OFF — test users are excluded unless an admin explicitly opts in to see them.
  includeTestUsers = false;

  async ready() {
    if (!this.user) return;
    const restored = this.restoreQueryFromUrl();
    if (restored) this.applyRequestToState(restored);
    this.fetch();
  }

  buildRequest(exportFlag = false): PlanProgressorMonitorGridRequest {
    const req: PlanProgressorMonitorGridRequest = {
      page: this.page,
      pageSize: this.pageSize,
      export: exportFlag,
      filters: {
        ...(this.filterHandback ? { HANDBACK: ['true'] } : {}),
        ...(this.filterTerminal ? { TERMINAL: ['true'] } : {}),
        ...(this.filterDisengage ? { DISENGAGE: ['true'] } : {}),
        ...(this.filterDormant ? { DORMANT: ['true'] } : {}),
        ...(this.filterRebaseline ? { REBASELINE: ['true'] } : {}),
        ...(this.filterRampWithoutDestination
          ? { RAMP_WITHOUT_DESTINATION: ['true'] }
          : {}),
        ...(this.statusFilterValues.length
          ? { STATUS: this.statusFilterValues }
          : {}),
        ...(this.userTagsFilterValues.length
          ? { USER_TAGS: this.userTagsFilterValues }
          : {}),
        ...(this.progressBucketFilterValues.length
          ? { PROGRESS_BUCKET: this.progressBucketFilterValues }
          : {}),
        EXCLUDE_TEST_USERS: [this.includeTestUsers ? 'false' : 'true'],
      },
    };
    const searchQuery: PlanProgressorMonitorGridRequest['searchQuery'] = {};
    if (this.expertTitleSearch.trim())
      searchQuery.EXPERT_TITLE = this.expertTitleSearch.trim();
    if (this.displayTitleSearch.trim())
      searchQuery.DISPLAY_TITLE = this.displayTitleSearch.trim();
    if (this.userEmailSearch.trim())
      searchQuery.USER_EMAIL = this.userEmailSearch.trim();
    if (this.partnerIdSearch.trim())
      searchQuery.PARTNER_ID = this.partnerIdSearch.trim();
    if (Object.keys(searchQuery).length) req.searchQuery = searchQuery;
    return req;
  }

  fetch(): void {
    this.tableLoading = true;
    const req = this.buildRequest(false);
    this.updateUrlWithQuery(req);
    const requestId = ++this.fetchRequestId;
    this.goalsSvc.getPlanProgressorMonitorGrid(req).subscribe({
      next: (res: PlanProgressorMonitorGridResponse) => {
        if (requestId !== this.fetchRequestId) return;
        this.data = res.data;
        this.totalRecords = res.totalRecords;
        this.pageSize = res.pageSize;
        this.tableLoading = false;
      },
      error: (err) => {
        if (requestId !== this.fetchRequestId) return;
        this.tableLoading = false;
        const errorBody = err.error as IAnyoError;
        this.toastrService.showAnyoErrorToast(
          errorBody?.description ?? 'Failed to load plan progressor data',
        );
      },
    });
  }

  applyFilters(): void {
    this.page = 1;
    this.fetch();
  }

  clearFilters(): void {
    this.filterHandback = false;
    this.filterTerminal = false;
    this.filterDisengage = false;
    this.filterDormant = false;
    this.filterRebaseline = false;
    this.filterRampWithoutDestination = false;
    this.expertTitleSearch = '';
    this.displayTitleSearch = '';
    this.userEmailSearch = '';
    this.partnerIdSearch = '';
    this.statusFilterValues = [];
    this.userTagsFilterValues = [];
    this.progressBucketFilterValues = [];
    this.includeTestUsers = false;
    this.page = 1;
    this.fetch();
  }

  onStatusFilterChange(values: string[]): void {
    this.statusFilterValues = values as UserObjectiveStatus[];
  }

  onUserTagsFilterChange(values: string[]): void {
    this.userTagsFilterValues = values;
  }

  onProgressBucketFilterChange(values: string[]): void {
    this.progressBucketFilterValues = values as UserObjectivesProgressBucket[];
  }

  nzPageIndexChange(p: number): void {
    this.page = p;
    this.fetch();
  }

  nzPageSizeChange(s: number): void {
    this.pageSize = s;
    this.fetch();
  }

  private updateUrlWithQuery(req: PlanProgressorMonitorGridRequest): void {
    const encoded = encodeURIComponent(JSON.stringify(req));
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { gridQuery: encoded },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private restoreQueryFromUrl(): PlanProgressorMonitorGridRequest | null {
    const qp = this.route.snapshot.queryParamMap.get('gridQuery');
    if (!qp) return null;
    try {
      const parsed: unknown = JSON.parse(decodeURIComponent(qp));
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        return null;
      }
      return parsed as PlanProgressorMonitorGridRequest;
    } catch {
      return null;
    }
  }

  private applyRequestToState(req: PlanProgressorMonitorGridRequest): void {
    if (typeof req.page === 'number') this.page = req.page;
    if (typeof req.pageSize === 'number') this.pageSize = req.pageSize;
    if (req.searchQuery?.EXPERT_TITLE)
      this.expertTitleSearch = req.searchQuery.EXPERT_TITLE;
    if (req.searchQuery?.DISPLAY_TITLE)
      this.displayTitleSearch = req.searchQuery.DISPLAY_TITLE;
    if (req.searchQuery?.USER_EMAIL)
      this.userEmailSearch = req.searchQuery.USER_EMAIL;
    if (req.searchQuery?.PARTNER_ID)
      this.partnerIdSearch = req.searchQuery.PARTNER_ID;
    const f = req.filters ?? {};
    this.filterHandback = !!f.HANDBACK?.length;
    this.filterTerminal = !!f.TERMINAL?.length;
    this.filterDisengage = !!f.DISENGAGE?.length;
    this.filterDormant = !!f.DORMANT?.length;
    this.filterRebaseline = !!f.REBASELINE?.length;
    this.filterRampWithoutDestination = !!f.RAMP_WITHOUT_DESTINATION?.length;
    this.statusFilterValues = (f.STATUS as UserObjectiveStatus[]) ?? [];
    this.userTagsFilterValues = f.USER_TAGS ?? [];
    this.progressBucketFilterValues =
      (f.PROGRESS_BUCKET as UserObjectivesProgressBucket[]) ?? [];
    this.includeTestUsers = f.EXCLUDE_TEST_USERS?.[0] === 'false';
  }
}
